import { AiDrawer, AliOssImage } from '@aigc/components';
import { useOverlayRef } from '@aigc/overlay-hook';
import { Button, Empty, Grid, Image, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { getAudioUrls, getImageUrls, getVideoUrls } from '../../file/utils';
import type { ScriptDto } from '../../script/script.dto';
import { TaskType, type VideoGenerationTaskDto } from '../../task/model';
import { GenerationType, videoModelSpecs } from '../../video-model/model-spec';
import { buildMultiPrompt } from '../../video/utils';

const Parameter = (props: { label: string; children: ReactNode }) => (
  <section className="min-w-0 space-y-2">
    <div className="font-medium">{props.label}</div>
    <div className="break-words">{props.children}</div>
  </section>
);

const Images = (props: { urls?: string[]; label: string }) => (
  <Image.PreviewGroup>
    <div className="flex flex-wrap gap-3">
      {props.urls?.length ? (
        props.urls.map((url, index) => (
          <AliOssImage
            key={`${index}-${url}`}
            src={url}
            size={120}
            mode="lfit"
            alt={`${props.label} ${index + 1}`}
          />
        ))
      ) : (
        <span>--</span>
      )}
    </div>
  </Image.PreviewGroup>
);

export const VideoGenerationDetailDrawer = (props: {
  task: VideoGenerationTaskDto;
  onGenerate: (task: VideoGenerationTaskDto) => void;
}) => {
  const overlayRef = useOverlayRef();
  const screen = Grid.useBreakpoint();
  const [isNavigating, setIsNavigating] = useState(false);
  const { task } = props;
  // 旧历史记录的生成参数可能不完整。
  const data: Partial<ScriptDto> | undefined = task.generate_data;
  const isLongVideo = task.type === TaskType.长视频生成;
  const isFrame = data?.generation_type === GenerationType.Frame;
  const canGenerate =
    !!data &&
    (data.generation_type === GenerationType.Reference ||
      (!isLongVideo && isFrame));
  const unavailableReason = !data
    ? '缺少原始生成参数，无法前往生成视频'
    : !canGenerate
      ? '暂不支持此生成类型，无法前往生成视频'
      : undefined;
  const prompt = data?.prompt?.trim()
    ? data.prompt
    : buildMultiPrompt(data?.segments, data?.extra_prompt);

  const handleGenerate = async () => {
    if (!canGenerate || isNavigating) return;
    setIsNavigating(true);
    await overlayRef.close();
    props.onGenerate(task);
  };

  const imageUrls = getImageUrls(data?.file_list);
  const videoUrls = getVideoUrls(data?.file_list);
  const audioUrls = getAudioUrls(data?.file_list);

  return (
    <AiDrawer
      title="视频生成参数"
      size={screen.lg ? '75%' : '100%'}
      classNames={{ body: 'min-h-0 overflow-auto lg:overflow-hidden' }}
      onClose={() => overlayRef.close()}
      footer={
        <div className="flex items-center justify-end gap-3">
          {unavailableReason && (
            <span className="text-primary-400">{unavailableReason}</span>
          )}
          <Button
            type="primary"
            disabled={!canGenerate}
            loading={isNavigating}
            onClick={handleGenerate}
          >
            去生成视频
          </Button>
        </div>
      }
    >
      <div className="flex min-h-0 flex-col gap-6 lg:h-full lg:flex-row">
        <div className="min-w-0 space-y-6 lg:w-[480px] lg:max-w-[50%] lg:flex-none lg:overflow-auto lg:pr-4">
          {isFrame ? (
            <div className="grid grid-cols-2 gap-4">
              <Parameter label="首帧图片">
                <Images urls={imageUrls?.slice(0, 1)} label="首帧图片" />
              </Parameter>
              <Parameter label="尾帧图片">
                <Images urls={imageUrls?.slice(1, 2)} label="尾帧图片" />
              </Parameter>
            </div>
          ) : (
            <>
              <Parameter label="产品名称">
                {data?.product_name || '--'}
              </Parameter>
              <Parameter label="参考图">
                <Images urls={imageUrls} label="参考图" />
              </Parameter>
              <Parameter label="参考视频">
                {videoUrls?.length ? (
                  <div className="space-y-3">
                    {videoUrls.map((url, index) => (
                      <video
                        key={`${index}-${url}`}
                        src={url}
                        controls
                        preload="metadata"
                        aria-label={`参考视频 ${index + 1}`}
                        className="max-h-60 w-full rounded-lg"
                      />
                    ))}
                  </div>
                ) : (
                  '--'
                )}
              </Parameter>
              <Parameter label="参考音频">
                {audioUrls?.length ? (
                  <div className="space-y-3">
                    {audioUrls.map((url, index) => (
                      <audio
                        key={`${index}-${url}`}
                        src={url}
                        controls
                        preload="metadata"
                        aria-label={`参考音频 ${index + 1}`}
                        className="w-full"
                      />
                    ))}
                  </div>
                ) : (
                  '--'
                )}
              </Parameter>
            </>
          )}
          <div className="grid grid-cols-2 gap-4">
            {!isLongVideo && (
              <Parameter label="生成平台">
                {videoModelSpecs.get(task.model)?.name ?? task.model}
              </Parameter>
            )}
            <Parameter label="宽高比">{data?.aspect_ratio || '--'}</Parameter>
            {!isLongVideo && (
              <Parameter label="时长">
                {data?.duration != null ? `${data.duration} 秒` : '--'}
              </Parameter>
            )}
          </div>
        </div>
        <section className="flex min-h-0 min-w-0 flex-1 flex-col">
          <Typography.Title level={5}>提示词</Typography.Title>
          <div className="min-h-0 flex-1 rounded-lg border border-primary-200 p-4 lg:overflow-auto">
            {prompt ? (
              <div className="whitespace-pre-wrap break-words">{prompt}</div>
            ) : (
              <Empty description="暂无提示词" />
            )}
          </div>
        </section>
      </div>
    </AiDrawer>
  );
};
